import { toast } from '@/shared/hooks/use-toast';
import type { ApiErrorResponse } from '@/shared/types/types';
import { getConfig } from './config';
import i18n from '@/i18n';

export enum ErrorType {
  NETWORK = 'network',
  API = 'api',
  VALIDATION = 'validation',
  AUTH = 'auth',
  NOT_FOUND = 'not_found',
  PERMISSION = 'permission',
  UNKNOWN = 'unknown'
}

export interface AppError {
  type: ErrorType;
  message: string;
  originalError?: Error;
  details?: Record<string, unknown>;
  retryable?: boolean;
}

// Chaves de i18n, não strings congeladas: resolvidas em cada acesso via
// i18n.t() (singleton), nunca no carregamento do módulo (LANDMINE do manual §2.6).
const ERROR_MESSAGE_KEYS: Record<ErrorType, string> = {
  [ErrorType.NETWORK]: 'common:errorHandler.network',
  [ErrorType.API]: 'common:errorHandler.api',
  [ErrorType.VALIDATION]: 'common:errorHandler.validation',
  [ErrorType.AUTH]: 'common:errorHandler.auth',
  [ErrorType.NOT_FOUND]: 'common:errorHandler.notFound',
  [ErrorType.PERMISSION]: 'common:errorHandler.permission',
  [ErrorType.UNKNOWN]: 'common:errorHandler.unknown'
};

const getErrorMessageText = (type: ErrorType): string => i18n.t(ERROR_MESSAGE_KEYS[type]);

export class GlobalErrorHandler {
  private static instance: GlobalErrorHandler;
  private get errorReportingEnabled(): boolean {
    if (import.meta.env.DEV) return false;
    try {
      return !!getConfig().appInsightsConnectionString;
    } catch {
      return false;
    }
  }

  private constructor() {
    this.setupGlobalHandlers();
  }

  public static getInstance(): GlobalErrorHandler {
    if (!GlobalErrorHandler.instance) {
      GlobalErrorHandler.instance = new GlobalErrorHandler();
    }
    return GlobalErrorHandler.instance;
  }

  private setupGlobalHandlers(): void {
    window.addEventListener('unhandledrejection', (event) => {
      if (event.reason == null) {
        event.preventDefault();
        return;
      }
      this.handleError(event.reason);
      event.preventDefault();
    });

    window.addEventListener('error', (event) => {
      if (event.error == null) {
        return;
      }
      this.handleError(event.error);
    });
  }

  public handleError(error: unknown, context?: Record<string, unknown>): AppError {
    const appError = this.normalizeError(error);

    if (this.errorReportingEnabled) {
      this.reportError(appError, context);
    }
    
    this.showErrorNotification(appError);
    
    return appError;
  }

  public handleApiError(error: unknown): AppError {
    if (this.isApiErrorResponse(error)) {
      return this.handleError({
        type: this.getApiErrorType(error),
        message: error.error.message,
        details: error.error.details,
        validation: 'validation' in error.error ? error.error.validation : undefined,
        retryable: this.isRetryableError(error)
      });
    }
    
    return this.handleError(error);
  }

  public handleNetworkError(error: Error): AppError {
    return this.handleError({
      type: ErrorType.NETWORK,
      message: i18n.t('common:errorHandler.networkFailure'),
      originalError: error,
      retryable: true
    });
  }

  public normalizeError(error: unknown): AppError {
    if (error == null) {
      return {
        type: ErrorType.UNKNOWN,
        message: getErrorMessageText(ErrorType.UNKNOWN),
        details: { originalError: 'null or undefined error' },
        retryable: false
      };
    }

    if (this.isAppError(error)) {
      return error;
    }

    if (this.isApiErrorResponse(error)) {
      return {
        type: this.getApiErrorType(error),
        message: error.error.message ?? getErrorMessageText(this.getApiErrorType(error)),
        details: error.error.details,
        retryable: this.isRetryableError(error)
      };
    }

    if (error instanceof Error) {
      return {
        type: this.categorizeError(error),
        message: this.getUserFriendlyMessage(error),
        originalError: error,
        retryable: this.isRetryableError(error)
      };
    }

    return {
      type: ErrorType.UNKNOWN,
      message: getErrorMessageText(ErrorType.UNKNOWN),
      details: { originalError: error },
      retryable: false
    };
  }

  private categorizeError(error: Error): ErrorType {
    const message = error.message.toLowerCase();
    
    if (message.includes('network') || message.includes('fetch') || message.includes('connection')) {
      return ErrorType.NETWORK;
    }
    
    if (message.includes('unauthorized') || message.includes('401')) {
      return ErrorType.AUTH;
    }
    
    if (message.includes('forbidden') || message.includes('403')) {
      return ErrorType.PERMISSION;
    }
    
    if (message.includes('not found') || message.includes('404')) {
      return ErrorType.NOT_FOUND;
    }
    
    return ErrorType.UNKNOWN;
  }

  private getApiErrorType(error: ApiErrorResponse): ErrorType {
    switch (error.status) {
      case 400:
        return ErrorType.VALIDATION;
      case 401:
        return ErrorType.AUTH;
      case 403:
        return ErrorType.PERMISSION;
      case 404:
        return ErrorType.NOT_FOUND;
      case 500:
      case 502:
      case 503:
      case 504:
        return ErrorType.API;
      default:
        return ErrorType.UNKNOWN;
    }
  }

  private getUserFriendlyMessage(error: Error): string {
    const errorType = this.categorizeError(error);
    return getErrorMessageText(errorType);
  }

  private isRetryableError(error: unknown): boolean {
    if (this.isApiErrorResponse(error)) {
      return error.status >= 500 && error.status < 600; 
    }
    
    if (error instanceof Error) {
      const message = error.message.toLowerCase();
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      return message.includes('network') || message.includes('timeout') || message.includes('connection');
    }
    
    return false;
  }

  private showErrorNotification(error: AppError): void {
    toast({
      variant: 'destructive',
      title: i18n.t('common:errorHandler.toastTitle'),
      description: error.message,
      duration: error.type === ErrorType.NETWORK ? 5000 : 3000
    });
  }

  private reportError(error: AppError, context?: Record<string, unknown>): void {
    // Importa dinamicamente o módulo appInsights (não bloqueia carregamento inicial)
    // Apenas chamado se errorReportingEnabled for true
    import('./appInsights').then(({ trackAppError }) => {
      trackAppError(error, context);
    }).catch((err) => {
      console.error('Erro ao carregegar módulo appInsights:', err);
    });
  }

  private isAppError(error: unknown): error is AppError {
    return typeof error === 'object' && 
           error !== null && 
           'type' in error && 
           'message' in error;
  }

  private isApiErrorResponse(error: unknown): error is ApiErrorResponse {
    return typeof error === 'object' && 
           error !== null && 
           'error' in error && 
           'status' in error;
  }
}

export const errorHandler = GlobalErrorHandler.getInstance();

export const withErrorHandling = <T extends unknown[], R>(
  fn: (...args: T) => Promise<R>
) => {
  return async (...args: T): Promise<R | null> => {
      try {
        return await fn(...args);
      } catch (error) {
        errorHandler.handleError(error);
        return null;
      }
    };
};

export const retryWithExponentialBackoff = async <T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> => {
  let lastError: unknown;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      
      if (attempt === maxRetries) {
        throw error;
      }
      
      const appError = errorHandler.normalizeError(error);
      if (!appError.retryable) {
        throw error;
      }
      
      const delay = baseDelay * Math.pow(2, attempt);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
};

export const useErrorHandler = () => {
  return {
    handleError: (error: unknown, context?: Record<string, unknown>) =>
      errorHandler.handleError(error, context),
    handleApiError: (error: unknown) => errorHandler.handleApiError(error),
    handleNetworkError: (error: Error) => errorHandler.handleNetworkError(error),
    withErrorHandling,
    retryWithExponentialBackoff
  };
};

export const getErrorMessage = (error: unknown, fallback: string = i18n.t('common:errorHandler.unknown')): string => {
  if (error == null) return fallback;

  if (typeof error === 'object') {
    const err = error as Record<string, unknown>;

    if (typeof err.message === 'string' && err.message) {
      return err.message;
    }

    if (err.error && typeof err.error === 'object') {
      const nestedError = err.error as Record<string, unknown>;
      if (typeof nestedError.message === 'string' && nestedError.message) {
        return nestedError.message;
      }
    }

    if (typeof err.detail === 'string' && err.detail) {
      return err.detail;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
};